import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useQuery } from '@tanstack/react-query';
import { passportApi } from '@/api/endpoints';
import { Button, Card, Field, ViewHeader } from '../components/ui';

/**
 * Мои данные — реальный профиль из useAuthStore и клубы из passportApi.
 * Поля переключателей уведомлений хранятся локально в localStorage
 * (у нас нет соответствующего backend-endpoint'а под клиентские prefs).
 */
const NOTIFY_KEY = 'cc-client-notify-prefs-v1';

interface NotifyPrefs {
  operatorReply: boolean;
  statusChange: boolean;
  emailDigest: boolean;
  marketing: boolean;
}

const DEFAULT_PREFS: NotifyPrefs = {
  operatorReply: true,
  statusChange: true,
  emailDigest: false,
  marketing: false,
};

function readPrefs(): NotifyPrefs {
  try {
    const raw = localStorage.getItem(NOTIFY_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<NotifyPrefs>) };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function ProfileView() {
  const { user } = useAuthStore();
  const { data: clubs } = useQuery({
    queryKey: ['my-clubs'],
    queryFn: passportApi.getMyClubs,
    staleTime: 60_000,
  });

  const [prefs, setPrefs] = useState<NotifyPrefs>(() => readPrefs());
  const togglePref = (k: keyof NotifyPrefs) => {
    setPrefs((c) => {
      const next = { ...c, [k]: !c[k] };
      try {
        localStorage.setItem(NOTIFY_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [clubId, setClubId] = useState<string>('');
  useEffect(() => {
    if (user) {
      setName(user.contactPerson || user.companyName || user.email || '');
      setPhone(user.phone || '');
    }
  }, [user]);
  useEffect(() => {
    if (clubs && clubs.length && !clubId) setClubId(clubs[0].id);
  }, [clubs, clubId]);

  const rows: Array<{ key: keyof NotifyPrefs; title: string; hint: string }> = [
    { key: 'operatorReply', title: 'Ответ оператора', hint: 'Push при новом сообщении в тикете' },
    { key: 'statusChange', title: 'Смена статуса', hint: 'Push когда меняется статус обращения' },
    { key: 'emailDigest', title: 'Email-дайджест', hint: 'Ежедневная сводка на почту' },
    { key: 'marketing', title: 'Маркетинг и новости', hint: 'Необязательные рассылки' },
  ];

  return (
    <div className="view active">
      <ViewHeader title="Мои данные" text="Профиль и настройки уведомлений." />
      <div className="two-col">
        <Card className="profile-card">
          <Field label="Имя контактного лица">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Email">
            <input className="input" value={user?.email || ''} disabled />
          </Field>
          <Field label="Телефон">
            <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+7 …" />
          </Field>
          <Field label="Клуб">
            <select className="input" value={clubId} onChange={(e) => setClubId(e.target.value)} disabled={!clubs?.length}>
              {(clubs || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              {!clubs?.length ? <option value="">Нет клубов</option> : null}
            </select>
          </Field>
          <Button className="btn-primary" disabled>
            <Save size={17} />
            Сохранить
          </Button>
          <div className="form-hint">
            Правка контактных данных сейчас через поддержку — свяжитесь с оператором,
            чтобы обновить имя/телефон/клуб.
          </div>
        </Card>
        <Card>
          <div className="card-head">
            <h3>Уведомления</h3>
          </div>
          {rows.map((row) => (
            <div className="switch-row" key={row.key}>
              <div>
                <h5>{row.title}</h5>
                <span>{row.hint}</span>
              </div>
              <button
                type="button"
                className={`switch ${prefs[row.key] ? 'on' : ''}`}
                aria-label={row.title}
                onClick={() => togglePref(row.key)}
              />
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
